import { useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, TextArea } from '@aimsos/ui';

import { buildQaDecision } from '../../../utils/hitlDecision.js';
import { listHitlRenderDetails } from '../../../utils/hitlCardMeta.js';
import { HitlMarkdown, OpenTasksLink, SectionLabel } from './HitlGateCardParts.jsx';

/**
 * Body for the approval gate (Approve / Reject + comments) and the
 * conversational Q&A gate (free-text answer, optional workspace research,
 * Continue). Owns the state only these modes use: `comments` and `qaResearch`.
 *
 * @param {object} props
 * @param {{ mode?: string, gate?: string, render?: object }} props.card
 * @param {ReturnType<import('../../../hooks/useHitlGateDecision.js').useHitlGateDecision>} props.decision
 * @param {(() => void)|null} [props.onOpenTasks]
 * @param {string} [props.translationNamespace='chat']
 */
function ApprovalQaGateBody({ card, decision, onOpenTasks = null, translationNamespace = 'chat' }) {
  const { t } = useTranslation(translationNamespace);
  const [comments, setComments] = useState('');
  // qa mode: let a free-text turn also request workspace research.
  const [qaResearch, setQaResearch] = useState(false);

  const { resolving, run, approve, reject, submit, task } = decision;
  const render = card?.render ?? {};
  const isQa = card.mode === 'qa';
  const approvalDetails = isQa ? [] : listHitlRenderDetails(render);

  const onApprove = () =>
    run(() => approve({ task, comments }), 'chat.hitl.approved', 'chat.hitl.approvedDetail');
  const onReject = () =>
    run(() => reject({ task, comments }), 'chat.hitl.rejected', 'chat.hitl.rejectedDetail');

  // Conversational (Q&A) gate — contract
  // {gate, done, message, research_requested, research_questions}.
  const qaAction = (done) => () =>
    submit({
      contractId: task.contractId,
      decision: buildQaDecision({
        gate: card.gate,
        done,
        message: comments,
        researchRequested: qaResearch,
      }),
    });
  const onSendAnswer = () => run(qaAction(false), 'chat.hitl.answerSent', 'chat.hitl.answerSentDetail');
  const onContinue = () => run(qaAction(true), 'chat.hitl.approved', 'chat.hitl.approvedDetail');

  return (
    <>
      {isQa ? (
        <>
          {render.agent_reply ? (
            <div>
              <SectionLabel>{t('chat.hitl.agentReply')}</SectionLabel>
              <HitlMarkdown content={render.agent_reply} />
            </div>
          ) : null}
          {render.client_questions ? (
            <div>
              <SectionLabel>{t('chat.hitl.openQuestions')}</SectionLabel>
              <HitlMarkdown content={render.client_questions} />
            </div>
          ) : null}
          {render.workspace_answers ? (
            <div>
              <SectionLabel>{t('chat.hitl.workspaceAnswers')}</SectionLabel>
              <HitlMarkdown content={render.workspace_answers} />
            </div>
          ) : null}
          {!render.agent_reply && !render.client_questions ? (
            <p className="text-sm whitespace-pre-wrap">
              {render.summary || t('chat.hitl.summaryFallback')}
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-sm whitespace-pre-wrap">
            {render.summary || t('chat.hitl.summaryFallback')}
          </p>
          {approvalDetails.map(({ key, value }) => (
            <div key={key}>
              <SectionLabel>{t(`chat.hitl.fields.${key}`, key.replace(/_/g, ' '))}</SectionLabel>
              <HitlMarkdown content={value} />
            </div>
          ))}
        </>
      )}

      <div>
        <label
          htmlFor="hitl-gate-comments"
          className="mb-1 block text-xs font-semibold uppercase tracking-wide opacity-60"
        >
          {t('chat.hitl.comments')}
        </label>
        <TextArea
          id="hitl-gate-comments"
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          placeholder={t(isQa ? 'chat.hitl.qaPlaceholder' : 'chat.hitl.commentsPlaceholder')}
        />
        {isQa ? (
          <label className="mt-2 flex items-center gap-2 text-xs text-content-secondary">
            <input
              type="checkbox"
              checked={qaResearch}
              onChange={(event) => setQaResearch(event.target.checked)}
            />
            {t('chat.hitl.searchWorkspace')}
          </label>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        {isQa ? (
          <>
            <Button onClick={onSendAnswer} disabled={resolving || (!comments.trim() && !qaResearch)}>
              {t('chat.hitl.sendAnswer')}
            </Button>
            <Button variant="secondary" onClick={onContinue} disabled={resolving}>
              {t('chat.hitl.continueBtn')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onApprove} disabled={resolving}>
              {t('chat.hitl.approve')}
            </Button>
            <Button variant="secondary" onClick={onReject} disabled={resolving}>
              {t('chat.hitl.reject')}
            </Button>
          </>
        )}
        {onOpenTasks ? (
          <div className="ml-auto">
            <OpenTasksLink onOpenTasks={onOpenTasks} label={t('chat.hitl.viewInTasks')} />
          </div>
        ) : null}
      </div>
    </>
  );
}

export default ApprovalQaGateBody;
